"use client";

import { ReactNode, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDetectOutsideClick } from "../hooks/useDetectOutsideClick";
import clsx from "clsx";

export interface Option {
  label: string;
  id?: string | number;
}

interface DropdownProps {
  className?: string;
  navClassName?: string;
  defaultValue: Option | undefined;
  value?: Option;
  isCustomOptions?: boolean;
  options: Option[] | ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  onChange?: (selectedOption: Option) => void;
  onAnimationComplete?: () => void;
}

export const DropdownSkeleton = ({ className }: { className?: string }) => (
  <div
    aria-busy="true"
    className={clsx(
      "relative w-full min-[550px]:max-w-[350px] h-11.25 p-2.5 pl-3.75 rounded-[10px] bg-tm-c-gray-800 animate-pulse flex items-center justify-between",
      className,
    )}
  >
    <span className="h-4 w-1/2 rounded bg-tm-c-gray-700" />
    <span className="size-5 rounded-full border-2 border-tm-c-gray-700 border-t-transparent animate-spin" />
  </div>
);

const Dropdown = ({
  className,
  navClassName,
  defaultValue,
  isCustomOptions = false,
  options,
  disabled = false,
  isLoading = false,
  onChange,
  onAnimationComplete,
  value,
}: DropdownProps) => {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useDetectOutsideClick({
    el: dropdownRef as any,
    initialState: false,
  });
  const [selected, setSelected] = useState<string>(defaultValue?.label || "");
  const [isArrowRotated, setIsArrowRotated] = useState(false);

  useEffect(() => {
    if (!!value) {
      setSelected(value.label);
    }
  }, [value]);

  useEffect(() => {
    setIsArrowRotated(isOpen);
  }, [isOpen]);

  const handleOpen = () => {
    if (isLoading) return;
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: Option) => {
    setSelected(option.label);
    onChange?.(option);
    setIsOpen(false);
  };

  return (
    <motion.div
      ref={dropdownRef}
      className={clsx(
        `relative w-full min-[550px]:max-w-[350px] h-11.25 outline-none p-2.5 pl-3.75
        bg-tm-c-gray-800 border-none cursor-pointer rounded-[10px]
        text-[15px] font-normal leading-6.25 tracking-[0.45px] text-tm-c-light
      `,
        `${className || ""}`,
        {
          "cursor-not-allowed opacity-60 pointer-events-none": disabled,
          "cursor-wait pointer-events-none animate-pulse": isLoading,
        },
      )}
      aria-busy={isLoading}
      animate={{
        borderBottomLeftRadius: isOpen ? 0 : 10,
        borderBottomRightRadius: isOpen ? 0 : 10,
      }}
      initial={{
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }}
      transition={{ ease: "linear", duration: 0.5 }}
      onClick={handleOpen}
    >
      <div className="flex items-center justify-between h-full">
        <div className="bg-tm-c-gray-800 flex items-center overflow-hidden whitespace-nowrap text-ellipsis">
          {selected}
        </div>
        {isLoading ? (
          <span
            role="status"
            aria-label="Loading"
            className="size-5 rounded-full border-2 border-current border-t-transparent animate-spin"
          />
        ) : (
          <motion.div
            animate={{
              rotate: isArrowRotated ? 180 : 0,
            }}
            transition={{
              ease: "easeInOut",
            }}
          >
            <ExpandMoreIcon fontSize="medium" />
          </motion.div>
        )}
      </div>
      <motion.nav
        initial={{ height: 0 }}
        animate={{ height: isOpen ? "auto" : 0 }}
        transition={{ duration: 0.4 }}
        onAnimationComplete={() => {
          if (!isOpen) {
            onAnimationComplete && onAnimationComplete();
          }
        }}
        className={clsx(
          `bg-tm-c-gray-900 absolute left-0 right-0 top-11.25 z-20 rounded-bl-[10px] rounded-br-[10px] overflow-hidden`,
          `${navClassName || ""}`,
        )}
        style={{ height: isOpen ? "auto" : 0 }}
      >
        {isCustomOptions ? (
          (options as ReactNode)
        ) : (
          <>
            {(options as Option[])?.map((option: any, index: number) => (
              <div
                key={`${option.id}-${index}`}
                className={`h-auto py-2.5 flex items-center px-3.75 pr-5 ${
                  option?.label === selected
                    ? "bg-[#4d4d4d]"
                    : "hover:bg-tm-c-gray-700 lg:hover:bg-tm-c-gray-700"
                }`}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </div>
            ))}
          </>
        )}
      </motion.nav>
    </motion.div>
  );
};

export default Dropdown;
